import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { ErrorBoundary } from "@sentry/react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/lib/auth";
import { ViewModeProvider } from "@/lib/view-mode-context";
import { UserSettingsProvider } from "@/lib/user-settings-context";
import { ThemeProvider } from "@/components/theme-provider";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppShell from "@/components/AppShell";
import PublicRouteHandler from "@/components/PublicRouteHandler";
import Auth from "./pages/Auth";
import Dashboard from "./pages/Dashboard";
import Players from "./pages/Players";
import NewSession from "./pages/NewSession";
import Sessions from "./pages/Sessions";
import SessionDetail from "./pages/SessionDetail";
import Settlement from "./pages/Settlement";
import SettingsPage from "./pages/SettingsPage";
import Expenses from "./pages/Expenses";
import ImportSession from "./pages/ImportSession";
import PlayerProfile from "./pages/PlayerProfile";
import Statistics from "./pages/Statistics";
import HeadToHead from "./pages/HeadToHead";
import WhoPayWho from "./pages/WhoPayWho";
import NotFound from "./pages/NotFound";
import ChangelogPage from "./pages/ChangelogPage";
import LiveSession from "./pages/LiveSession";

// ✅ ADD THIS IMPORT
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem themes={["light", "dark", "midnight", "forest", "sunset", "rose", "system"]}>
      <AuthProvider>
        <ViewModeProvider>
          <UserSettingsProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <SentryRoutes>
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/changelog" element={<ChangelogPage />} />
                  <Route path="/live/:sessionId" element={<LiveSession />} />
                  <Route path="/public/:userId" element={<PublicRouteHandler />}>
                    <Route index element={<Dashboard />} />
                    <Route path="players" element={<Players />} />
                    <Route path="sessions" element={<Sessions />} />
                    <Route path="session/:id" element={<SessionDetail />} />
                    <Route path="statistics" element={<Statistics />} />
                    <Route path="head-to-head" element={<HeadToHead />} />
                    <Route path="player/:id" element={<ErrorBoundary fallback={<div className="p-4 text-center">Error loading profile</div>}><PlayerProfile /></ErrorBoundary>} />
                    <Route path="expenses" element={<Expenses />} />
                    <Route path="settlement" element={<Settlement />} />
                    <Route path="who-pays-who" element={<WhoPayWho />} />
                  </Route>
                  <Route element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
                    <Route path="/" element={<Dashboard />} />
                    <Route path="/players" element={<Players />} />
                    <Route path="/session/new" element={<NewSession />} />
                    <Route path="/session/import" element={<ImportSession />} />
                    <Route path="/sessions" element={<Sessions />} />
                    <Route path="/player/:id" element={<ErrorBoundary fallback={<div className="p-4 text-center">Error loading profile</div>}><PlayerProfile /></ErrorBoundary>} />
                    <Route path="/session/:id" element={<SessionDetail />} />
                    <Route path="/statistics" element={<Statistics />} />
                    <Route path="/head-to-head" element={<HeadToHead />} />
                    <Route path="/settlement" element={<Settlement />} />
                    <Route path="/who-pays-who" element={<WhoPayWho />} />
                    <Route path="/expenses" element={<Expenses />} />
                    <Route path="/settings" element={<SettingsPage />} />
                  </Route>
                  <Route path="*" element={<NotFound />} />
                </SentryRoutes>
              </BrowserRouter>

              {/* ✅ ADD THIS AT ROOT LEVEL */}
              <Analytics />
              <SpeedInsights />

            </TooltipProvider>
          </UserSettingsProvider>
        </ViewModeProvider>
      </AuthProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;